// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { stripZerosLeft } from 'ethers';
import { useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DepositAttestation } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Attestation/Deposit/module';
import Loading from '../../../components/Loading';
import { DataTable } from '../../../components/Table/DataTable';
import useBridgeAgreement from '../../../hooks/queries/bridge/agreement/useBridgeAgreement';
import useBridgeUserAgreement from '../../../hooks/queries/bridge/agreement/useBridgeUserAgreement';
import useDepositAttestations from '../../../hooks/queries/bridge/deposit/useDepositAttestations';
import TemplateContract from '../../../utils/TemplateContract';
import { fmt } from '../../../utils/common';
import { MintFromAttestationDialog } from './MintFromAttestation';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import useMintFromAttestation from '../../../hooks/mutations/bridge/deposits/useMintFromAttestation';
import { useParties } from '../../../contexts/PartiesContext';
import PartyDisplay from '../../../components/PartyDisplay';

type Row = {
  contractId: string;
  state: string;
  payload: TemplateContract<DepositAttestation>;
  reason?: string;
};

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'amount',
      headerName: 'Amount',
      valueGetter: (params) => fmt(params.row.payload.payload.amount, 6),
      flex: 100,
      filterable: false,
    },
    {
      field: 'recipient',
      headerName: 'Recipient',
      valueGetter: (params) => <PartyDisplay party={params.row.payload.payload.recipient} />,
      flex: 100,
      filterable: false,
    },
    {
      field: 'sourceAddress',
      headerName: 'Depositor Address',
      valueGetter: (params) => stripZerosLeft(`0x${params.row.payload.payload.sourceDepositor}`),
      flex: 250,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 100,
      filterable: false,
    },
  ];
};

export const Deposits = () => {
  const [onMint, setOnMint] = useState<TemplateContract<DepositAttestation> | undefined>();
  const bridgeAgreement = useBridgeAgreement();
  const { party } = useParties();
  const bridgeUserAgreement = useBridgeUserAgreement();
  const depositAttestations = useDepositAttestations();
  const mintFromAttestation = useMintFromAttestation();

  const isLoading =
    bridgeAgreement.isLoading ||
    bridgeUserAgreement.isLoading ||
    depositAttestations.isLoading ||
    mintFromAttestation.isLoading ||
    !depositAttestations.data;
  if (isLoading) return <Loading />;

  const rows = depositAttestations.data.map((r) => ({
    contractId: r.contractId as string,
    state: 'Locked on Source Chain',
    payload: r,
  }));

  const createActions = (r: Row) => {
    const actions: Action[] = [];
    if (!!r.payload && r.payload.payload.recipient === party) {
      actions.push({ name: 'Mint', action: () => Promise.resolve(setOnMint(r.payload)) });
    }
    return actions;
  };

  return (
    <>
      {!!onMint && (
        <MintFromAttestationDialog
          open={!!onMint}
          onClose={() => setOnMint(undefined)}
          attestations={[onMint]}
        />
      )}
      <DataTable
        title="Deposits"
        enableTableWrapper
        variant="h3"
        columns={createHeaders(createActions)}
        rows={rows}
        rowKey={(a) => a.contractId}
      />
    </>
  );
};
